<template>
  <div class="party-fee-page">
    <SubPageHeader title="党费缴纳" />
    <div class="content">
      <div v-if="isLoading" class="state-text">加载中...</div>
      <div v-else-if="partyFeeList.length === 0" class="state-text">暂无党费缴纳数据</div>
      <div v-else class="list">
        <div
          v-for="(item, index) in partyFeeList"
          :key="item.id || item.dangfei_id || item.record_id || index"
          class="fee-card"
        >
          <div class="card-main">
            <div class="card-row">
              <span class="label">缴费周期：</span>
              <span class="value">{{ getFeePeriod(item) }}</span>
            </div>
            <div class="card-row">
              <span class="label">待缴金额：</span>
              <span class="value amount-text">{{ getFeeAmount(item) }}</span>
            </div>
            <div class="card-row">
              <span class="label">逾期时间：</span>
              <span class="value">{{ getOverdueTime(item) }}</span>
            </div>
            <div class="card-row">
              <span class="label">缴费状态：</span>
              <span :class="['status-tag', getPayStatusClass(item)]">{{ getPayStatus(item) }}</span>
            </div>
            <div class="card-row">
              <span class="label">审核状态：</span>
              <span :class="['status-tag', getAuditStatusClass(item)]">{{ getAuditStatus(item) }}</span>
            </div>
            <div v-if="isRejectedStatus(item)" class="card-row reject-row">
              <span class="label">驳回原因：</span>
              <span class="value reject-reason">{{ getRejectReason(item) }}</span>
            </div>
          </div>
          <div v-if="shouldShowPayButton(item)" class="card-action">
            <el-button class="pay-btn" type="primary" size="small" @click="handlePay(item)">缴费</el-button>
          </div>
        </div>
      </div>
    </div>

    <el-dialog
      v-model="payDialogVisible"
      title="缴费"
      width="88%"
      append-to-body
      :close-on-click-modal="false"
      @closed="handleDialogClosed"
    >
      <div v-if="detailLoading" class="dialog-state">加载中...</div>
      <div v-else-if="!detailData" class="dialog-state">暂无缴费详情</div>
      <div v-else class="dialog-content">
        <div class="dialog-item">
          <div class="dialog-label">缴费周期</div>
          <div class="dialog-value">{{ getFeePeriod(detailData) }}</div>
        </div>
        <div class="dialog-item">
          <div class="dialog-label">待缴金额</div>
          <div class="dialog-value">{{ getFeeAmount(detailData) }}</div>
        </div>
        <div class="dialog-item">
          <div class="dialog-label">缴费二维码</div>
          <div class="dialog-value qr-wrapper">
            <el-image
              v-if="getQrCodeUrl(detailData)"
              :src="getQrCodeUrl(detailData)"
              :preview-src-list="[getQrCodeUrl(detailData)]"
              :preview-teleported="true"
              fit="contain"
              class="qr-image"
            />
            <span v-else>--</span>
          </div>
          <div v-if="getQrCodeUrl(detailData)" class="qr-actions">
            <el-button class="download-btn" plain size="small" @click="downloadQrCode">
              下载二维码
            </el-button>
          </div>
        </div>
        <div class="dialog-item">
          <div class="dialog-label">支付凭证</div>
          <div class="dialog-value">
            <el-upload
              class="voucher-upload"
              accept="image/*"
              :show-file-list="false"
              :http-request="handleUploadRequest"
              :disabled="submitLoading || uploadLoading"
            >
              <div :class="['upload-trigger', { 'upload-trigger-empty': !submitForm.payImage }]">
                <img v-if="submitForm.payImage" :src="submitForm.payImage" alt="支付凭证" class="voucher-image" />
                <div v-else class="upload-placeholder">
                  <div class="upload-plus">+</div>
                  <div>{{ uploadLoading ? '上传中...' : '点击上传凭证' }}</div>
                </div>
              </div>
            </el-upload>
          </div>
        </div>
        <div class="dialog-item">
          <div class="dialog-label">缴费状态</div>
          <div class="dialog-value">
            <span :class="['status-tag', getPayStatusClass(detailData)]">{{ getPayStatus(detailData) }}</span>
          </div>
        </div>
      </div>
      <template #footer>
        <div class="dialog-footer">
          <el-button class="cancel-btn" @click="payDialogVisible = false">取消</el-button>
          <el-button
            class="confirm-btn"
            type="primary"
            :loading="submitLoading"
            :disabled="uploadLoading || detailLoading"
            @click="submitPayVoucher"
          >
            确认提交
          </el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import {
  getPartyFeeList,
  getPartyFeeDetail,
  submitPartyFee,
  uploadFile,
} from '@/api'
import toast from '@/components/toast'
import SubPageHeader from '../component/SubPageHeader.vue'

export default {
  name: 'PartyFeePayPage',
  components: {
    SubPageHeader
  },
  data() {
    return {
      isLoading: false,
      partyFeeList: [],
      payDialogVisible: false,
      detailLoading: false,
      submitLoading: false,
      uploadLoading: false,
      currentRecordId: null,
      detailData: null,
      submitForm: {
        payImage: ''
      }
    }
  },
  mounted() {
    this.fetchPartyFeeList()
  },
  methods: {
    async fetchPartyFeeList() {
      this.isLoading = true
      try {
        const res = await getPartyFeeList()
        this.partyFeeList = this.normalizeList(res?.data)
      } catch (error) {
        this.partyFeeList = []
      } finally {
        this.isLoading = false
      }
    },
    normalizeList(payload) {
      if (Array.isArray(payload)) {
        return payload
      }
      if (Array.isArray(payload?.list)) {
        return payload.list
      }
      if (Array.isArray(payload?.rows)) {
        return payload.rows
      }
      return []
    },
    normalizeDetail(payload) {
      if (!payload) {
        return null
      }
      if (Array.isArray(payload)) {
        return payload[0] || null
      }
      return payload?.detail || payload?.info || payload
    },
    getRecordId(item) {
      return item?.id || item?.dangfei_id || item?.record_id || null
    },
    getFeePeriod(item) {
      const periodStart = item?.periodStart || item?.period_start
      const periodEnd = item?.periodEnd || item?.period_end
      if (periodStart && periodEnd) {
        return `${periodStart} 至 ${periodEnd}`
      }
      if (periodStart) {
        return periodStart
      }
      return item?.payPeriod || item?.period || item?.fee_period || item?.pay_cycle || '--'
    },
    getFeeAmount(item) {
      const amount = item?.waitPayAmount ?? item?.pending_amount ?? item?.amount ?? item?.pay_amount
      if (amount === undefined || amount === null || amount === '') {
        return '--'
      }
      if (typeof amount === 'number') {
        return `¥${amount.toFixed(2)}`
      }
      return String(amount).startsWith('¥') ? String(amount) : `¥${amount}`
    },
    getOverdueTime(item) {
      return item?.overdueTime || item?.overdue_time || item?.expire_time || item?.periodEnd || item?.period_end || '--'
    },
    getPayStatus(item) {
      const rawStatus = item?.payStatus ?? item?.pay_status
      if (rawStatus === 0 || rawStatus === '0') {
        return '待缴费'
      }
      if (rawStatus === 1 || rawStatus === '1') {
        return '已缴费'
      }
      return item?.payStatusName || item?.pay_status_name || rawStatus || '--'
    },
    getAuditStatus(item) {
      const rawStatus = item?.auditStatus ?? item?.audit_status
      if (rawStatus === 0 || rawStatus === '0') {
        return '待审核'
      }
      if (rawStatus === 1 || rawStatus === '1') {
        return this.getRejectReason(item) !== '--' ? '审核驳回' : '审核通过'
      }
      if (rawStatus === 2 || rawStatus === '2') {
        return '审核驳回'
      }
      return item?.auditStatusName || item?.audit_status_name || rawStatus || '--'
    },
    getRejectReason(item) {
      return item?.auditRemark || item?.audit_remark || item?.rejectReason || item?.reject_reason || item?.auditReason || item?.audit_reason || '--'
    },
    getQrCodeUrl(item) {
      return item?.collectionQrCode || item?.collection_qr_code || item?.payQrcode || item?.pay_qrcode || item?.qrcode || item?.qr_code || ''
    },
    isPendingPayStatus(item) {
      const rawStatus = item?.payStatus ?? item?.pay_status
      if (rawStatus === 0 || rawStatus === '0') {
        return true
      }
      const statusText = this.getPayStatus(item)
      return /待缴费|未缴费|待支付/.test(String(statusText))
    },
    isPaidStatus(item) {
      const rawStatus = item?.payStatus ?? item?.pay_status
      if (rawStatus === 1 || rawStatus === '1') {
        return true
      }
      const statusText = this.getPayStatus(item)
      return /已缴费|已支付/.test(String(statusText))
    },
    isRejectedStatus(item) {
      const rawStatus = item?.auditStatus ?? item?.audit_status
      if (rawStatus === 2 || rawStatus === '2') {
        return true
      }
      if ((rawStatus === 1 || rawStatus === '1') && this.getRejectReason(item) !== '--') {
        return true
      }
      const auditText = this.getAuditStatus(item)
      return /审核驳回|驳回/.test(String(auditText))
    },
    getPayStatusClass(item) {
      if (this.isPaidStatus(item)) {
        return 'status-success'
      }
      if (this.isPendingPayStatus(item)) {
        return 'status-warning'
      }
      return 'status-default'
    },
    getAuditStatusClass(item) {
      if (this.isRejectedStatus(item)) {
        return 'status-danger'
      }
      const rawStatus = item?.auditStatus ?? item?.audit_status
      if (rawStatus === 1 || rawStatus === '1') {
        return 'status-success'
      }
      if (rawStatus === 0 || rawStatus === '0') {
        return 'status-warning'
      }
      return 'status-default'
    },
    shouldShowPayButton(item) {
      if (this.isRejectedStatus(item)) {
        return true
      }
      if (this.isPaidStatus(item)) {
        return false
      }
      return this.isPendingPayStatus(item)
    },
    async handlePay(item) {
      const id = this.getRecordId(item)
      if (!id) {
        toast('缺少缴费记录ID')
        return
      }
      this.currentRecordId = id
      this.payDialogVisible = true
      this.detailLoading = true
      this.detailData = null
      this.submitForm.payImage = ''
      try {
        const res = await getPartyFeeDetail(id)
        const detail = this.normalizeDetail(res?.data)
        this.detailData = detail
        let existingPayImage = detail?.payImage || detail?.pay_image || ''
        const rawStatus = detail?.auditStatus ?? detail?.audit_status
        if (rawStatus === 2 || rawStatus === '2') {
          existingPayImage = ''
        }
        this.submitForm.payImage = existingPayImage
      } catch (error) {
        this.detailData = null
      } finally {
        this.detailLoading = false
      }
    },
    extractUploadedUrl(payload) {
      if (!payload) {
        return ''
      }
      if (typeof payload === 'string') {
        return payload
      }
      const root = payload?.data || payload
      const cloudUrl = root?.cloudResult?.url || root?.cloud_result?.url
      if (cloudUrl) {
        return cloudUrl
      }
      const directUrl = root?.url || root?.fileUrl || root?.file_url || root?.path
      if (directUrl) {
        return directUrl
      }
      const fullPath = root?.fullPath || root?.full_path
      if (!fullPath) {
        return ''
      }
      if (/^https?:\/\//.test(fullPath)) {
        return fullPath
      }
      return fullPath.startsWith('/') ? fullPath : `/${fullPath}`
    },
    async handleUploadRequest(option) {
      this.uploadLoading = true
      try {
        const res = await uploadFile(option.file)
        const fileUrl = this.extractUploadedUrl(res)
        if (!fileUrl) {
          throw new Error('未获取到上传地址')
        }
        this.submitForm.payImage = fileUrl
        option.onSuccess?.(res)
      } catch (error) {
        option.onError?.(error)
      } finally {
        this.uploadLoading = false
      }
    },
    async submitPayVoucher() {
      if (!this.currentRecordId) {
        toast('缺少缴费记录ID')
        return
      }
      if (!this.submitForm.payImage) {
        toast('请先上传支付凭证')
        return
      }
      this.submitLoading = true
      try {
        await submitPartyFee({
          id: Number(this.currentRecordId),
          payImage: this.submitForm.payImage
        })
        toast('提交成功')
        this.payDialogVisible = false
        this.fetchPartyFeeList()
      } finally {
        this.submitLoading = false
      }
    },
    downloadQrCode() {
      const qrUrl = this.getQrCodeUrl(this.detailData)
      if (!qrUrl) {
        toast('暂无可下载的二维码')
        return
      }
      const link = document.createElement('a')
      link.href = qrUrl
      link.download = `dangfei-qrcode-${Date.now()}.png`
      link.target = '_blank'
      link.rel = 'noopener noreferrer'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    handleDialogClosed() {
      this.detailData = null
      this.currentRecordId = null
      this.submitForm.payImage = ''
    }
  }
}
</script>

<style scoped lang="scss">
.party-fee-page {
  min-height: 100vh;
  background: #ffffff;
}

.content {
  padding: 3.58974vw 3.58974vw 5.12821vw;
}

.state-text {
  padding: 10.25641vw 0;
  text-align: center;
  color: #999;
  font-size: 3.84615vw;
}

.list {
  display: flex;
  flex-direction: column;
  gap: 3.07692vw;
}

.fee-card {
  position: relative;
  display: flex;
  justify-content: space-between;
  gap: 3.07692vw;
  background: #ffffff;
  border-radius: 3.58974vw;
  padding: 4.10256vw 3.58974vw 3.58974vw;
  box-shadow: 0 1.4vw 4.2vw rgba(174, 49, 49, 0.12);
  border: 0.25641vw solid #ffe3e3;
}

.card-main {
  flex: 1;
  min-width: 0;
}

.card-row {
  margin-top: 1.53846vw;
  display: flex;
  align-items: flex-start;
  font-size: 3.48718vw;
  line-height: 1.5;
}

.card-row:first-child {
  margin-top: 0;
}

.label {
  width: 18vw;
  flex-shrink: 0;
  color: #a48484;
}

.value {
  color: #3d2a2a;
  font-weight: 500;
  word-break: break-all;
}

.amount-text {
  color: #e04b4b;
  font-weight: 700;
}

.reject-reason {
  color: #df4747;
}

.reject-row {
  margin-top: 1.02564vw;
}

.status-tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 14.87179vw;
  height: 6.15385vw;
  padding: 0 2.30769vw;
  border-radius: 3.07692vw;
  font-size: 3.07692vw;
  font-weight: 600;
  line-height: 1;
  border: 0.25641vw solid transparent;
}

.status-success {
  color: #2f955d;
  background: #ecf9f1;
  border-color: #b8e3c9;
}

.status-warning {
  color: #be7b13;
  background: #fff6e6;
  border-color: #f4d4a0;
}

.status-danger {
  color: #d64545;
  background: #fff1f1;
  border-color: #f4b3b3;
}

.status-default {
  color: #6a7282;
  background: #f4f6fa;
  border-color: #e3e8f2;
}

.card-action {
  display: flex;
  align-items: center;
  padding-top: 2.05128vw;
}

.pay-btn {
  min-width: 16.92308vw;
  height: 8.71795vw;
  border: none;
  border-radius: 4.61538vw;
  font-size: 3.84615vw;
  background: linear-gradient(135deg, #ff5b5b 0%, #ff7474 55%, #ff8a5e 100%);
  box-shadow: 0 1.02564vw 2.30769vw rgba(255, 96, 96, 0.35);
}

.dialog-state {
  padding: 8vw 0;
  text-align: center;
  color: #9d7e7e;
}

.dialog-content {
  display: flex;
  flex-direction: column;
  gap: 2.5641vw;
}

.dialog-item {
  display: flex;
  flex-direction: column;
  gap: 1.02564vw;
  padding: 2.5641vw 2.30769vw;
  border-radius: 2.5641vw;
  background: #ffffff;
}

.dialog-label {
  font-size: 3.33333vw;
  color: #a08484;
}

.dialog-value {
  font-size: 3.84615vw;
  color: #3d2a2a;
  line-height: 1.6;
  word-break: break-all;
}

.qr-wrapper {
  display: flex;
  justify-content: flex-start;
}

.qr-actions {
  margin-top: 2.05128vw;
  display: flex;
  justify-content: flex-start;
}

.qr-image {
  width: auto;
  height: auto;
  max-width: 100%;
  border-radius: 2.5641vw;
  border: none;
  background: transparent;
  padding: 0;
  box-shadow: none;
}

.voucher-upload {
  width: auto;
  max-width: 100%;
}

.upload-trigger {
  width: auto;
  height: auto;
  min-height: 0;
  max-width: 100%;
  border: none;
  border-radius: 0;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  overflow: hidden;
  transition: all 0.2s ease;
}

.upload-trigger-empty {
  min-width: 34.87179vw;
  min-height: 22.5641vw;
  border: 0.25641vw solid #ffd7d7;
  border-radius: 2.5641vw;
  background: linear-gradient(180deg, #fffefe 0%, #fff6f6 100%);
  padding: 2.30769vw 3.07692vw;
  cursor: pointer;
  box-shadow: inset 0 0 0 0.25641vw rgba(255, 96, 96, 0.04);
  justify-content: center;
  align-items: center;
}

.upload-trigger-empty:hover {
  border-color: #ffbcbc;
  background: linear-gradient(180deg, #fffdfd 0%, #fff1f1 100%);
}

.voucher-image {
  width: auto;
  height: auto;
  max-width: 100%;
  object-fit: contain;
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  text-align: center;
  gap: 1.28205vw;
  color: #bd4b4b;
  font-size: 3.23077vw;
  font-weight: 500;
  line-height: 1.4;
}

.upload-plus {
  width: 7.17949vw;
  height: 7.17949vw;
  border-radius: 50%;
  border: none;
  background: linear-gradient(135deg, #ff6363 0%, #ff8b8b 100%);
  color: #ffffff;
  font-size: 4.87179vw;
  line-height: 7.17949vw;
  text-align: center;
  box-shadow: 0 0.76923vw 1.79487vw rgba(255, 96, 96, 0.22);
}

.dialog-footer {
  width: 100%;
  display: flex;
  justify-content: flex-end;
  gap: 2.05128vw;
}

.cancel-btn {
  min-width: 17.4359vw;
  border-radius: 4.61538vw;
}

.confirm-btn {
  min-width: 20.51282vw;
  border: none;
  border-radius: 4.61538vw;
  background: linear-gradient(135deg, #ff5b5b 0%, #ff7474 55%, #ff8a5e 100%);
  box-shadow: 0 1.02564vw 2.30769vw rgba(255, 96, 96, 0.3);
}

.download-btn {
  border-radius: 3.58974vw;
  border-color: #ffc2c2;
  color: #df4f4f;
}
</style>
